import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mandatory } from "../../../../../utils/Functions";

const CustomCheckboxField = ({ name, label, isDisabled = false }) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={false}
      render={({ field: { value, onChange, ...field } }) => (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "grey.700",
            borderRadius: "4px",
            padding: "14px 14px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            "&:hover": { borderColor: "grey.500" },
            "&:focus-within": {
              borderColor: "primary.main",
              borderWidth: "2px",
            },
          }}
          onClick={() => !isDisabled && onChange(!value)}
        >
          <Typography
            variant="body1"
            sx={{
              mr: 1,
              color: isDisabled ? "text.disabled" : "text.secondary",
            }}
          >
            {t(label)}
          </Typography>
          <Checkbox
            {...field}
            checked={!!value}
            onChange={(e) => {
              e.stopPropagation();
              onChange(e.target.checked);
            }}
            disabled={isDisabled}
            sx={{ p: 0 }}
          />
        </Box>
      )}
    />
  );
};

export default function InpatientStayInfoStep() {
  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { t } = useTranslation();
  const fivFelvOptions = ["+/-", "+/+", "-/+", "-/-", "N/A"];

  function disableDateOfExit(dateOfExit) {
    const dateOfEntrance = getValues("dateOfEntrance");
    return dayjs(dateOfExit).isBefore(dayjs(dateOfEntrance));
  }

  return (
    <Card variant="elevation" elevation={2}>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MedicalInformationIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("StayInformation")}
            </Typography>
          </Stack>

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="dateOfEntrance"
                  control={control}
                  rules={{ required: t("EmptyField") }}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth error={!!errors.dateOfEntrance}>
                      <MobileDatePicker
                        label={mandatory(t("DateOfEntrance"))}
                        value={value ?? null}
                        onChange={(newValue) => {
                          onChange(newValue);
                          const exitDate = getValues("dateOfExit");
                          if (
                            exitDate &&
                            dayjs(exitDate).isBefore(dayjs(newValue))
                          ) {
                            setValue("dateOfExit", null);
                          }
                        }}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.dateOfEntrance,
                            helperText: errors.dateOfEntrance?.message,
                          },
                        }}
                      />
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="fivFelv"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error}>
                      <InputLabel id="fiv-felv-select-label">
                        {t("FIV/FELV")}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="fiv-felv-select-label"
                        label={t("FIV/FELV")}
                      >
                        <MenuItem value="" disabled>
                          {t("Select")}
                        </MenuItem>
                        {fivFelvOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="dateOfExit"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth error={!!errors.dateOfExit}>
                      <MobileDatePicker
                        shouldDisableDate={(date) => disableDateOfExit(date)}
                        label={t("DateOfExit")}
                        value={value ?? null}
                        onChange={onChange}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.dateOfExit,
                            helperText: errors.dateOfExit?.message,
                          },
                        }}
                      />
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("Ampoule")}
                  {...register("ampoule")}
                  inputProps={{ maxLength: 120 }}
                  error={!!errors.ampoule}
                  helperText={errors.ampoule?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomCheckboxField name="corporal" label="Corporal" />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
